import axios from 'axios';
import {omit} from 'lodash';

import {SessionFeedback} from 'interfaces';
import {InviteeToken} from 'services/InviteeToken';
import {
    ClientStartSessionConnectParams, ClientStartSessionParams, ClientSettings, ClientAssignmentInfo, RequestAppointmentDetails,
    PreSessionFileSide, PreSessionFlowMode
} from 'types';
import {platformUtils} from 'utils';

import {API_URL} from './constants';

const actionTypes = {
    LEAVE_SESSION: 'leave_session',
    GET_STATE: 'get_state',
    GET_ACTIVE_SESSION_INFO: 'get_active_session_info',
    PHOTO_VALIDATION_RESULT: 'photo_validation_result',
    START_SESSION: 'start_session',
    START_REASSIGNED_SESSION: 'start_reassigned_session',
    ADD_ACTIVITY_DATA: 'add_activity_data',
    GET_ACTIVITY_FILE: 'get_activity_file',
    START_SESSION_CONNECT: 'start_session_connect',
    REQUEST_APPOINTMENT: 'request_appointment',
    UPDATE_ASYNC_REQUEST: 'update_async_request',
    NOTIFY: 'notify',
    VALIDATE_TOKEN: 'validate_token',
    GET_JOIN_SESSION_SETTINGS: 'get_join_session_settings',
    ADD_FEEDBACK: 'add_feedback',
    SUBMIT_PRE_SESSION_FORM: 'submit_pre_session_form',
    SUBMIT_PRE_SESSION_SCREENSHOT: 'submit_pre_session_screenshot',
    SUBMIT_PRE_SESSION_FILE: 'submit_pre_session_file',
    START_PRE_SESSION_ASYNC_REQUEST: 'start_pre_session_async_request',
    SKIP_PRE_SESSION_STEP: 'skip_pre_session_step',
    CONFIRM_PRE_SESSION_FLOW: 'confirm_pre_session_flow',
    SET_PRE_SESSION_FLOW_MODE: 'set_pre_session_flow_mode',
};

const actionsWithTokenInParams = [
    actionTypes.REQUEST_APPOINTMENT, actionTypes.ADD_FEEDBACK, actionTypes.START_REASSIGNED_SESSION
];
const actionsWithOptionalTokenInParams = [
    actionTypes.NOTIFY
];
const getToken = (action: string, params?: any): string | null => {
    if (action === actionTypes.START_SESSION) {
        return null;
    }
    if (actionsWithTokenInParams.includes(action)) {
        return params?.inviteeToken;
    }
    return InviteeToken.get() || (actionsWithOptionalTokenInParams.includes(action) ? params?.inviteeToken : null);
};

const executeAction = (action: string, payload?: any, params?: any) => {
    const token = getToken(action, params);
    return axios.post(
        `${API_URL}/actions/client`,
        {payload},
        {
            params: {action, ...Object.assign({}, params, {inviteeToken: undefined})},
            headers: {Authorization: `Bearer ${token}`}
        }
    ).then((res) => res && res.data && (res.data.payload || {}));
};

// Actions
const getClientState = (params: {sessionId: string, participantId: string, settings?: boolean}) =>
    executeAction(actionTypes.GET_STATE, null, params);

const requestAppointment = (details: RequestAppointmentDetails, inviteeToken: string) =>
    executeAction(actionTypes.REQUEST_APPOINTMENT, details, {inviteeToken});

const startSession = (data: ClientStartSessionParams) => {
    const payload = {
        clientInfo: platformUtils.getPlatformInfo(),
        ...data
    };

    return executeAction(actionTypes.START_SESSION, payload);
};
const addActivityData = (params: {sessionId: string, participantId: string}, id: string, data: any) =>
    executeAction(actionTypes.ADD_ACTIVITY_DATA, {id, data}, params);

const getActivityFile = (fileName: string, activityId: string) => executeAction(actionTypes.GET_ACTIVITY_FILE, {fileName, activityId});

const startSessionConnect = (data: ClientStartSessionConnectParams) => {
    const payload = {
        clientInfo: platformUtils.getPlatformInfo(),
        ...(omit(data, 'token'))
    };

    return executeAction(actionTypes.START_SESSION_CONNECT, payload, {token: data.token});
};

const startReassignedSession = (params: {sessionId: string, participantId: string}, inviteeToken: string) =>
    executeAction(actionTypes.START_REASSIGNED_SESSION, null, {...params, inviteeToken});

const notifyClientSession = (
    params: {sessionId: string, participantId: string}, payload: {name: string, data: any}, inviteeToken?: string
) => executeAction(actionTypes.NOTIFY, payload, {...params, inviteeToken});

const updateParticipantAsyncRequest = (params: {sessionId: string, participantId: string}, payload: any) =>
    executeAction(actionTypes.UPDATE_ASYNC_REQUEST, payload, params);

const leaveSession = (params: {sessionId: string, participantId: string}) =>
    executeAction(actionTypes.LEAVE_SESSION, null, params);

const addFeedback = (params: {sessionId: string, participantId: string}, feedback: SessionFeedback, inviteeToken: string) =>
    executeAction(actionTypes.ADD_FEEDBACK, {feedback}, {...params, inviteeToken});

const validateToken = (): Promise<{isValid: boolean}> => executeAction(actionTypes.VALIDATE_TOKEN);

const getJoinSessionSettings = (assignment: ClientAssignmentInfo): Promise<Partial<ClientSettings>> =>
    executeAction(actionTypes.GET_JOIN_SESSION_SETTINGS, assignment);

const submitPreSessionForm = (
    params: {sessionId: string, participantId: string},
    payload: {stepId: string, values: Record<string, string>}
) => executeAction(actionTypes.SUBMIT_PRE_SESSION_FORM, payload, params);

const submitPreSessionScreenshot = (
    params: {sessionId: string, participantId: string},
    payload: {stepId: string, base64: string}
) => executeAction(actionTypes.SUBMIT_PRE_SESSION_SCREENSHOT, payload, params);

const submitPreSessionFile = (
    params: {sessionId: string, participantId: string},
    payload: {stepId: string, side: PreSessionFileSide, base64: string}
) => executeAction(actionTypes.SUBMIT_PRE_SESSION_FILE, payload, params);

const startPreSessionAsyncRequest = (
    params: {sessionId: string, participantId: string},
    payload: {stepId: string}
) => executeAction(actionTypes.START_PRE_SESSION_ASYNC_REQUEST, payload, params);

const skipPreSessionStep = (
    params: {sessionId: string, participantId: string},
    payload: {stepId: string}
) => executeAction(actionTypes.SKIP_PRE_SESSION_STEP, payload, params);

const setPreSessionFlowMode = (
    params: {sessionId: string, participantId: string}, payload: {mode: PreSessionFlowMode}
) => executeAction(actionTypes.SET_PRE_SESSION_FLOW_MODE, payload, params);

const confirmPreSessionFlow = (
    params: {sessionId: string, participantId: string}
) => executeAction(actionTypes.CONFIRM_PRE_SESSION_FLOW, {}, params);

export const clientActionsApi = {
    startSession,
    startSessionConnect,
    startReassignedSession,
    getClientState,
    addActivityData,
    getActivityFile,
    notifyClientSession,
    requestAppointment,
    updateParticipantAsyncRequest,
    leaveSession,
    addFeedback,
    validateToken,
    getJoinSessionSettings,
    submitPreSessionForm,
    submitPreSessionScreenshot,
    submitPreSessionFile,
    startPreSessionAsyncRequest,
    skipPreSessionStep,
    confirmPreSessionFlow,
    setPreSessionFlowMode
};
